// ProjectPortfolio.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { SearchBar } from "./components/search-bar";
import { SearchResults } from "./components/search-results";
import HistorySidebar from "./components/history-sidebar";
import {
  saveSearchHistory,
  appendChatToExistingHistory,
  getSearchHistory,
  getSearchById,
  deleteSearchHistory,
} from "./api";

const ProjectPortfolio = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();

  const [chatHistory, setChatHistory] = useState([]);
  const [currentInput, setCurrentInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [historyList, setHistoryList] = useState([]);


  useEffect(() => {
    async function loadHistory() {
      const histories = await getSearchHistory();
      setHistoryList(histories);
    }
    loadHistory();
  }, []);


  useEffect(() => {
    if (chatId) {
      handleHistorySelect(chatId);
    } else {
      setChatHistory([]);
      setSelectedHistoryId(null);
    }
  }, [chatId]);

  const handleInputChange = (e) => {
    setCurrentInput(e.target.value);
    if (error) setError("");
  };

  const handleKeyDown = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      await handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!currentInput.trim()) {
      setError("Please enter your requirement");
      return;
    }

    const userMessage = { role: "user", message: currentInput.trim() };
    setChatHistory((prev) => [...prev, userMessage]);
    setCurrentInput("");
    setIsLoading(true);

    try {
      const response = await axios.post(
        "https://gmtuzmclz3wzou6ck3mslsbmw40yhegk.lambda-url.ap-south-1.on.aws/ai/Project_Agent",
        { query: userMessage.message }
      );

      const aiMessage = response.data?.project_recommendations;
      console.log("ai response", aiMessage);
      
      const relevancyMessage = response.data?.relevancy_message;
      const relevancyCategory = response.data?.relevancy_category;

      const hasResults =
        aiMessage &&
        ((Array.isArray(aiMessage) && aiMessage.length > 0) ||
          (!Array.isArray(aiMessage) && typeof aiMessage === "string" && aiMessage.trim() !== ""));

      const aiResponse = {
        role: "ai",
        message: hasResults ? aiMessage : "No relevant results found.",
        relevancyMessage,
        relevancyCategory
      };
      setChatHistory((prev) => [...prev, aiResponse]);

      if (!selectedHistoryId) {
        const created = await saveSearchHistory({
          prompt: userMessage.message,
          results: aiMessage,
          relevancyMessage,
          relevancyCategory,
        });
        setSelectedHistoryId(created.id);
        setHistoryList((prev) => [created, ...prev]);
        navigate(`/explore/${created.id}`); // update URL
      } else {
        await appendChatToExistingHistory(selectedHistoryId, {
          prompt: userMessage.message,
          results: aiMessage,
          relevancyMessage,
          relevancyCategory,
        });
      }
    } catch (err) {
      setChatHistory((prev) => [
        ...prev,
        { role: "ai", message: "Failed to fetch results. Please try again." },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleHistorySelect = async (id) => {
    if (!id) {
      setChatHistory([]);
      setSelectedHistoryId(null);
      navigate("/explore");
      return;
    }

    setSelectedHistoryId(id);
    navigate(`/explore/${id}`);
    setIsHistoryLoading(true);

    const data = await getSearchById(id);
    setIsHistoryLoading(false);

    if (data && data.project_search_history_chat) {
      const restoredChats = data.project_search_history_chat.flatMap((chatItem) => {
        if (Array.isArray(chatItem) && chatItem.length > 0) {
          const innerChat = chatItem[0];
          return [
            { role: "user", message: innerChat.project_query },
            {
              role: "ai",
              message: innerChat.project_response?.project_recommendations || "No results found.",
              relevancyMessage: innerChat.project_response?.relevancy_message,
              relevancyCategory: innerChat.project_response?.relevancy_category,
            },
          ];
        }
        return [];
      });
      setChatHistory(restoredChats);
    } else {
      setChatHistory([]);
    }
  };

  const handleDeleteHistory = async (id) => {
    await deleteSearchHistory(id);
    setHistoryList((prev) => prev.filter((item) => item.id !== id));
    if (selectedHistoryId === id) {
      setSelectedHistoryId(null);
      setChatHistory([]);
      navigate("/explore");
    }
  };

  return (
    <div className="h-[calc(100vh-64px)] flex w-full">
      <HistorySidebar
        historyList={historyList}
        onSelect={handleHistorySelect}
        selectedId={selectedHistoryId}
        onDelete={handleDeleteHistory}
      />

      <div className={`flex-1 flex flex-col ${chatHistory.length === 0 && !isLoading ? "justify-center items-center" : ""}`}>
        <div className={`p-6 space-y-4 ${chatHistory.length > 0 || isLoading ? "flex-1 overflow-y-auto" : ""}`}>
          {isHistoryLoading ? (
            <div className="flex justify-center items-center h-full w-full">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900" />
            </div>
          ) : (
            <>
              {chatHistory.length === 0 && !isLoading && (
                <div className="text-center text-gray-400">
                  Start a conversation about your project needs!
                </div>
              )}
              {chatHistory.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  {msg.role === "user" ? (
                    <div className="max-w-[70%] px-4 py-2 rounded-lg shadow text-base whitespace-pre-line bg-zinc-200/50 self-end">
                      {msg.message}
                    </div>
                  ) : (
                    <div className="max-w-[70%] self-start">
                      <SearchResults
                        results={Array.isArray(msg.message) ? msg.message : [msg.message]}
                        relevancyMessage={msg.relevancyMessage}
                        relevancyCategory={msg.relevancyCategory}
                      />
                    </div>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="max-w-[70%] px-4 py-2 rounded-lg bg-white border text-gray-400 animate-pulse">
                    Searching...
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className={`flex justify-center p-4 ${chatHistory.length > 0 || isLoading ? "" : "w-full"} flex gap-2`}>
          <SearchBar
            requirement={currentInput}
            error={error}
            onSubmit={handleSubmit}
            onChange={handleInputChange}
            isLoading={isLoading}
            onKeyDown={handleKeyDown}
          />
        </div>

        {error && <div className="text-red-500 text-center py-2">{error}</div>}
      </div>
    </div>
  );
};

export default ProjectPortfolio;
